import React, { useEffect, useState, useReducer, useContext, useRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  DialogTitle,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  IconButton,
  Box,
  Alert,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import { Delete, AccessTime, Person } from '@mui/icons-material';
import { format } from 'date-fns';
import { IContainer, IContainerCreate, IContainerDimensionUOM, ILengthUOM } from '../../model';
import {
  addContainer,
  updateContainer,
  deleteContainer,
  calculateTankVolume,
  ITankVolumeCalculatorResponse,
} from '../services';
import { ContainersContext } from '../contexts';
import { convertLengthValue } from '../../utils';

export interface FormCreateEditContainerProps {
  container?: Partial<IContainer>;
  onClose: () => void;
}

const dialogTransitionDuration = 250;

interface IFormState {
  tankName: string;
  isActive: boolean;
  tankTypeId: number | null;
  distanceUOM: ILengthUOM | null;
  dimensions: IContainerDimensionUOM[];
}

type Action =
  | { type: 'SET_FIELD'; field: keyof IFormState; value: any }
  | { type: 'SET_TANK_TYPE'; tankTypeId: number; volumeUOM: IContainerDimensionUOM[] }
  | { type: 'SET_MEASUREMENT_VALUE'; id: number; value: number }
  | { type: 'INITIALIZE_FORM'; isEdit: boolean; data: Partial<IContainer> }
  | { type: 'CLEAR_FORM' };

const reducer = (state: IFormState, action: Action): IFormState => {
  switch (action.type) {
    case 'SET_MEASUREMENT_VALUE':
      return {
        ...state,
        dimensions: state.dimensions.map((m) =>
          m.edgelink_tank_dimension_id === action.id ? { ...m, value: action.value } : m,
        ),
      };
    case 'SET_TANK_TYPE': {
      // Create a mapping of existing dimension values by edgelink_tank_dimension_id
      const existingValues = {};
      state.dimensions.forEach((dim) => {
        if (dim.value !== undefined && dim.value !== null) {
          existingValues[dim.edgelink_tank_dimension_id] = dim.value;
        }
      });

      // Apply existing values to the new dimensions where possible
      const newDimensions = action.volumeUOM.map((dim) => ({
        ...dim,
        value:
          existingValues[dim.edgelink_tank_dimension_id] !== undefined
            ? existingValues[dim.edgelink_tank_dimension_id]
            : dim.value,
      }));

      return {
        ...state,
        tankTypeId: action.tankTypeId,
        dimensions: newDimensions,
      };
    }
    case 'SET_FIELD':
      if (action.field === 'distanceUOM' && state.distanceUOM && action.value) {
        // When changing UOM, convert all dimension values
        const fromUnit = state.distanceUOM.name;
        const toUnit = action.value.name;

        // Only convert values if units are different
        if (fromUnit !== toUnit && state.dimensions.length > 0) {
          const updatedDimensions = state.dimensions.map((dim) => {
            if (dim.value !== undefined && dim.value !== null) {
              // Convert the value from previous unit to new unit
              const convertedValue = convertLengthValue(dim.value, fromUnit, toUnit);
              return {
                ...dim,
                value: Number(convertedValue.toFixed(4)), // Round to 4 decimal places for display
              };
            }
            return dim;
          });

          return {
            ...state,
            [action.field]: action.value,
            dimensions: updatedDimensions,
          };
        }
      }

      return {
        ...state,
        [action.field]: action.value,
      };
    case 'INITIALIZE_FORM': {
      // When editing, convert dimension values from millimeters to the target UOM
      let initialDimensions = action.isEdit ? action.data.dimensions || [] : [];
      const targetUOM = action.isEdit ? action.data.distance_uom : null;

      // If we have both dimensions and a target UOM, perform conversion
      if (action.isEdit && initialDimensions.length > 0 && targetUOM) {
        initialDimensions = initialDimensions.map((dimension) => {
          if (dimension.value !== undefined && dimension.value !== null) {
            // Convert from millimeters (DB storage) to the target UOM
            const convertedValue = convertLengthValue(dimension.value, 'millimeters', targetUOM.name);
            return {
              ...dimension,
              value: Number(convertedValue.toFixed(4)), // Round to 4 decimal places for display
            };
          }
          return dimension;
        });
      }

      return {
        tankName: action.isEdit ? action.data.tank_name || '' : '',
        isActive: action.isEdit ? action.data.status !== false : true,
        tankTypeId: action.isEdit ? action.data.edgelink_tank_type_id || null : null,
        distanceUOM: targetUOM,
        dimensions: initialDimensions,
      };
    }
    case 'CLEAR_FORM':
      return {
        tankName: '',
        isActive: true,
        tankTypeId: null,
        distanceUOM: null,
        dimensions: [],
      };
    default:
      return state;
  }
};

const FormCreateEditContainer: React.FunctionComponent<FormCreateEditContainerProps> = (props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'Create' | 'Edit'>();
  const { refreshContainers, tankTypes, distanceUOMs } = useContext(ContainersContext);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [volumeCalculation, setVolumeCalculation] = useState<ITankVolumeCalculatorResponse | null>(null);
  const [calculatingVolume, setCalculatingVolume] = useState<boolean>(false);
  const [volumeError, setVolumeError] = useState<string | null>(null);
  const volumeDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [formState, dispatch] = useReducer(reducer, {
    tankName: props.container?.tank_name || '',
    isActive: props.container?.status !== false,
    tankTypeId: props.container?.edgelink_tank_type_id || null,
    distanceUOM: props.container?.distance_uom || null,
    dimensions: props.container?.dimensions || [],
  });

  useEffect(() => {
    const isEdit = props.container?.tank_config_id !== undefined;
    setActionType(isEdit ? 'Edit' : 'Create');

    dispatch({ type: 'INITIALIZE_FORM', isEdit, data: props.container || {} });

    setOpen(props.container !== undefined);
  }, [props.container]);

  useEffect(() => {
    if (formState.tankTypeId === undefined) return;
    const tank = tankTypes.find((t) => t.edgelink_tank_type_id === formState.tankTypeId);
    if (tank) {
      dispatch({ type: 'SET_TANK_TYPE', tankTypeId: tank.edgelink_tank_type_id, volumeUOM: tank.dimensions });
    }
  }, [formState.tankTypeId, tankTypes]);

  // Calculate tank volume when dimensions or UOM changes, with debounce
  useEffect(() => {
    // Clear previous timer if exists
    if (volumeDebounceTimer.current) {
      clearTimeout(volumeDebounceTimer.current);
    }

    // Reset volume calculation if we're missing required data
    if (!formState.tankTypeId || !formState.distanceUOM || formState.dimensions.length === 0) {
      setVolumeCalculation(null);
      setVolumeError(null);
      return;
    }

    // Check if all dimensions have values
    const allDimensionsHaveValues = formState.dimensions.every(
      (dim) => dim.value !== undefined && dim.value !== null && dim.value > 0,
    );

    if (!allDimensionsHaveValues) {
      setVolumeCalculation(null);
      setVolumeError(null);
      return;
    }
    // Start debounce timer for volume calculation
    volumeDebounceTimer.current = setTimeout(async () => {
      try {
        setCalculatingVolume(true);
        setVolumeError(null);

        // Get tank type name
        const tankType = tankTypes.find((t) => t.edgelink_tank_type_id === formState.tankTypeId);
        if (!tankType) {
          throw new Error('Tank type not found');
        }

        // Map dimension values to the format expected by the API, converting to millimeters
        const dimensionsMap: Record<string, number> = {};
        formState.dimensions.forEach((dim) => {
          // Convert value to millimeters regardless of what unit is selected in the UI
          const valueInMm = formState.distanceUOM
            ? convertLengthValue(dim.value || 0, formState.distanceUOM.name, 'millimeters')
            : dim.value || 0;
          dimensionsMap[dim.name] = valueInMm;
        });

        // Call the API - always using millimeters as the input unit
        const result = await calculateTankVolume({
          shape: tankType.tank_type_name,
          dimensions: dimensionsMap,
          input_unit: 'millimeters', // Always use millimeters as input unit
          output_unit: 'gallons',
          tank_type_name: tankType.tank_type_name,
        });

        setVolumeCalculation(result);
      } catch (error) {
        console.error('Error calculating volume:', error);
        setVolumeError(error instanceof Error ? error.message : 'Failed to calculate volume');
        setVolumeCalculation(null);
      } finally {
        setCalculatingVolume(false);
      }
    }, 1000); // 1 second debounce

    // Cleanup function to clear timeout
    // eslint-disable-next-line consistent-return
    return () => volumeDebounceTimer.current && clearTimeout(volumeDebounceTimer.current);
  }, [formState.dimensions, formState.distanceUOM, formState.tankTypeId, tankTypes]);

  const handleClose = async () => {
    setOpen(false);
    setTimeout(() => {
      props.onClose();
      dispatch({ type: 'CLEAR_FORM' });
    }, dialogTransitionDuration + 10);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Convert all dimensions to millimeters for storage
      const convertedDimensions = formState.dimensions.map((d) => {
        const valueInMm = formState.distanceUOM
          ? convertLengthValue(d.value || 0, formState.distanceUOM.name, 'millimeters')
          : d.value || 0;
        return {
          edgelink_tank_dimension_id: d.edgelink_tank_dimension_id,
          value: valueInMm,
        };
      });

      const containerData: IContainerCreate = {
        tank_name: formState.tankName,
        edgelink_tank_type_id: formState.tankTypeId,
        distance_uom_id: formState.distanceUOM?.edgelink_sensor_uom_id || 0,
        dimensions: convertedDimensions,
      };

      if (actionType === 'Create') {
        await addContainer(containerData);
      } else if (actionType === 'Edit' && props.container?.tank_config_id) {
        // For Edit mode, call updateContainer with the existing container ID and updated data
        const updates: Partial<IContainer> = {
          tank_name: formState.tankName,
          edgelink_tank_type_id: formState.tankTypeId,
          status: formState.isActive,
          distance_uom: formState.distanceUOM,
          dimensions: convertedDimensions.map((d, index) => ({
            edgelink_tank_dimension_id: d.edgelink_tank_dimension_id,
            value: d.value,
            name: formState.dimensions[index]?.name, // Preserve dimension name
          })),
        };
        await updateContainer(props.container.tank_config_id, updates);
      }

      await refreshContainers();
      handleClose();
    } catch (error) {
      console.error('Error submitting container data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCapacityText = () => {
    if (calculatingVolume) return '--';
    if (volumeCalculation) return `${volumeCalculation.total_volume.toFixed(2)} gallons`;
    return '--';
  };

  const getButtonText = () => {
    if (isSubmitting) return 'SAVING...';
    return actionType === 'Edit' ? 'UPDATE CONTAINER' : 'CREATE CONTAINER';
  };

  const handleDelete = async () => {
    if (!props.container?.tank_config_id) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const success = await deleteContainer(props.container.tank_config_id);
      if (success) {
        setShowDeleteConfirm(false);
        await refreshContainers();
        handleClose();
      } else {
        setDeleteError('Unable to delete container. It may be in use or a system default.');
      }
    } catch (error) {
      console.error('Error deleting container:', error);
      setDeleteError('An error occurred while deleting the container.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        transitionDuration={dialogTransitionDuration}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: (theme) => theme.palette.grey[50],
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            py: 2,
            px: 3,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="subtitle1" fontWeight="bold">
                {actionType} Container
              </Typography>
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  label="Active"
                  control={
                    <Switch
                      checked={formState.isActive}
                      onChange={() => dispatch({ type: 'SET_FIELD', field: 'isActive', value: !formState.isActive })}
                    />
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Form Group for Basic Information */}
            <Grid item xs={12} md={12}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Tank Name"
                      name="tank_name"
                      value={formState.tankName}
                      onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'tankName', value: e.target.value })}
                      fullWidth
                      required
                      error={formState.tankName === ''}
                      helperText={formState.tankName === '' ? 'Tank name is required' : ''}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="tank-type-label">Tank Type</InputLabel>
                      <Select
                        labelId="tank-type-label"
                        id="tank-type-select"
                        name="tank_type_id"
                        value={formState.tankTypeId}
                        label="Tank Type"
                        onChange={(e) =>
                          dispatch({ type: 'SET_FIELD', field: 'tankTypeId', value: e.target.value as number })
                        }
                        error={formState.tankTypeId === null}
                      >
                        {tankTypes.map((tank) => (
                          <MenuItem key={tank.edgelink_tank_type_id} value={tank.edgelink_tank_type_id}>
                            {tank.tank_type_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="uom-label">Unit of Measurement</InputLabel>
                      <Select
                        name="uom_id"
                        value={formState.distanceUOM?.edgelink_sensor_uom_id}
                        label="Unit of Measurement"
                        onChange={(e) =>
                          dispatch({
                            type: 'SET_FIELD',
                            field: 'distanceUOM',
                            value: distanceUOMs.find((uom) => uom.edgelink_sensor_uom_id === e.target.value),
                          })
                        }
                        error={formState.distanceUOM === null}
                      >
                        {distanceUOMs.map((uom) => (
                          <MenuItem key={uom.edgelink_sensor_uom_id} value={uom.edgelink_sensor_uom_id}>
                            {uom.name} ({uom.abbreviation})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>

            {/* Form Group for Measurement Numbers */}
            <Grid item xs={12} md={12}>
              <FormControl component="fieldset" fullWidth>
                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Measurement Numbers
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="medium"
                          color={calculatingVolume ? 'text.secondary' : 'primary.main'}
                        >
                          Total Capacity:{' '}
                          {calculatingVolume ? (
                            <Box
                              component="span"
                              sx={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
                            >
                              <CircularProgress size={16} thickness={4} sx={{ ml: 0.5 }} />
                            </Box>
                          ) : (
                            getCapacityText()
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    {volumeError && (
                      <Typography variant="subtitle1" color="error">
                        {volumeError}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  {formState.dimensions && formState.distanceUOM !== null
                    ? formState.dimensions.map((measurement) => (
                        // eslint-disable-next-line react/jsx-indent
                        <Grid item xs={12} md={6} key={measurement.edgelink_tank_dimension_id}>
                          <TextField
                            label={`${measurement.name} (${formState.distanceUOM?.abbreviation})`}
                            type="number"
                            value={measurement.value ?? ''}
                            onChange={(e) =>
                              dispatch({
                                type: 'SET_MEASUREMENT_VALUE',
                                id: measurement.edgelink_tank_dimension_id,
                                value: parseFloat(e.target.value),
                              })
                            }
                            fullWidth
                            required
                            inputProps={{ min: 0, step: 0.01 }}
                            error={!measurement.value || measurement.value <= 0}
                            helperText={
                              !measurement.value || measurement.value <= 0
                                ? 'Measurement value must be greater than 0'
                                : undefined
                            }
                          />
                        </Grid>
                      ))
                    : null}
                </Grid>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        {actionType === 'Edit' && props.container?.created_at && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              px: 3,
              py: 1.5,
              bgcolor: (theme) => theme.palette.grey[50],
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              color: (theme) => theme.palette.text.secondary,
              fontSize: '1.25rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
              <AccessTime fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
              {props.container.updated_at
                ? format(new Date(props.container.updated_at), 'MMM d, yyyy h:mm a')
                : format(new Date(props.container.created_at), 'MMM d, yyyy h:mm a')}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Person fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
              {props.container.updated_by || props.container.created_by || 'System'}
            </Box>
          </Box>
        )}
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            bgcolor: (theme) => theme.palette.grey[50],
          }}
        >
          <Box>
            {actionType === 'Edit' && (
              <Button
                startIcon={<Delete />}
                variant="text"
                color="error"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={props.container?.is_system_default}
                sx={{ mr: 2 }}
              >
                DELETE
              </Button>
            )}
          </Box>
          <Box>
            <Button variant="outlined" color="error" onClick={handleClose} sx={{ mr: 2, minWidth: '100px' }}>
              CANCEL
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} sx={{ minWidth: '150px' }}>
              {getButtonText()}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: (theme) => theme.palette.grey[50],
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            py: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Delete Container
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete the container{' '}
            <Typography component="span" fontWeight="bold">
              {props.container?.tank_name}
            </Typography>
            ?
          </DialogContentText>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'medium' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: (theme) => theme.palette.grey[50],
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
            variant="outlined"
            sx={{ minWidth: '100px' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting || props.container?.is_system_default}
            sx={{ minWidth: '100px' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormCreateEditContainer;
